import axios, { type AxiosInstance } from "axios";
import Cookies from "universal-cookie";
import { settings } from "@/lib/utils/settings/settings";
import { refreshToken } from "./AuthService";
import ErrorHandler from "./ErrorService/ErrorService";

export type IHttpClient = AxiosInstance;

type AuthInfo = {
	access_token: string;
	refresh_token: string;
	expires_in: number;
	id_token?: string;
	token_type?: string;
};

export class HttpClient {
	private constructor() {}

	private static readonly COOKIE_NAME = "tokens";
	private static cookies = new Cookies();
	private static instance: IHttpClient;
	private static isRefreshing = false;

	private static failedQueue: {
		resolve: (token: string | null) => void;
		reject: (error: Error) => void;
	}[] = [];

	static async refreshAccessToken(): Promise<AuthInfo> {
		const cleanup = HttpClient.withRefreshing();

		try {
			const tokens = HttpClient.getTokens();
			if (!tokens || !tokens.refresh_token) {
				return HttpClient.handleTokenError(
					new Error("No tokens available to refresh"),
				);
			}

			const newTokens = await refreshToken(tokens.refresh_token);

			const updatedTokens: AuthInfo = {
				...tokens,
				access_token: newTokens?.access_token || tokens.access_token,
				expires_in: newTokens.expires_in,
				refresh_token: newTokens?.refresh_token || tokens.refresh_token,
			};

			if (!HttpClient.validateTokens(updatedTokens)) {
				throw new Error("Invalid token response");
			}

			HttpClient.setAuthorization(updatedTokens);
			return updatedTokens;
		} catch (error) {
			return HttpClient.handleTokenError(error as Error);
		} finally {
			cleanup();
		}
	}

	private static withRefreshing() {
		HttpClient.isRefreshing = true;
		return () => {
			HttpClient.isRefreshing = false;
		};
	}

	private static getInstance(baseURL: string): AxiosInstance {
		const result = axios.create({
			baseURL,
		});

		result.interceptors.request.use(
			async (config) => {
				const tokens = HttpClient.getTokens();
				if (tokens) {
					if (!HttpClient.validateTokens(tokens)) {
						HttpClient.logout();
						return HttpClient.handleTokenError(
							new Error("Incomplete or invalid tokens"),
						);
					}

					const expirationTime = tokens.expires_in;
					const currentTime = Date.now();

					if (currentTime > expirationTime) {
						if (!HttpClient.isRefreshing) {
							HttpClient.isRefreshing = true;
							try {
								const newTokens = await HttpClient.refreshAccessToken();
								config.headers.Authorization = `Bearer ${newTokens.access_token}`;
								HttpClient.processQueue(null, newTokens.access_token);
								return config;
							} catch (error) {
								HttpClient.processQueue(error, null);
								HttpClient.logout?.();
								return HttpClient.handleTokenError(error as Error);
							}
						}
						return new Promise((resolve, reject) => {
							HttpClient.failedQueue.push({ resolve, reject });
						})
							.then((token) => {
								config.headers.Authorization = `Bearer ${token}`;
								return config;
							})
							.catch((error) => {
								return HttpClient.handleTokenError(error as Error);
							});
					}
					config.headers.Authorization = `Bearer ${tokens.access_token}`;
				}
				return config;
			},
			(error) => Promise.reject(error),
		);

		result.interceptors.response.use(
			(response) => response,
			async (error) => {
				if (error.response && error.response.status === 401) {
					try {
						const originalRequest = error.config;
						const newTokens = await HttpClient.refreshAccessToken();
						originalRequest.headers.Authorization = `Bearer ${newTokens.access_token}`;
						return axios(originalRequest);
					} catch (refreshError) {
						return HttpClient.handleTokenError(refreshError as Error);
					}
				}
				return HttpClient.handleTokenError(error);
			},
		);

		return result;
	}

	static create(): IHttpClient {
		if (!HttpClient.instance) {
			HttpClient.instance = HttpClient.getInstance(settings.apiHost);
		}
		return HttpClient.instance;
	}

	static getTokens(): AuthInfo | undefined {
		return HttpClient.cookies.get(HttpClient.COOKIE_NAME);
	}

	static setAuthorization(tokens: AuthInfo) {
		if (!tokens.access_token || !tokens.refresh_token) {
			throw new Error("Invalid token structure");
		}
		const bearerToken = `Bearer ${tokens.access_token}`;
		HttpClient.create().defaults.headers.common.Authorization = bearerToken;

		const expirationTime = Date.now() + tokens.expires_in * 1000;

		HttpClient.cookies.set(
			HttpClient.COOKIE_NAME,
			{
				...tokens,
				expires_in: expirationTime,
			},
			{ path: "/" },
		);
	}

	private static processQueue(error: unknown, token: string | null = null) {
		for (const prom of HttpClient.failedQueue) {
			if (error) {
				prom.reject(error as Error);
			} else {
				prom.resolve(token);
			}
		}

		HttpClient.failedQueue = [];
	}

	private static handleTokenError(error: Error): Promise<never> {
		ErrorHandler.logError(error);
		return Promise.reject(error);
	}

	private static validateTokens(tokens: AuthInfo): boolean {
		const isValid =
			!!tokens.access_token &&
			!!tokens.refresh_token &&
			typeof tokens.expires_in === "number";
		return isValid;
	}

	static logout() {
		const clientId = settings.oauthClientId;
		const redirectUri = `${window.location.origin}`;
		const authUrl = `${settings.authUrl}/logout?response_type=code&client_id=${clientId}&logout_uri=${redirectUri}`;
		HttpClient.cookies.remove(HttpClient.COOKIE_NAME, { path: "/" });
		setTimeout(() => {
			window.location.href = authUrl;
		}, 300);
	}
}
